import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import {
  adjustMomentForTimezone,
  formatDate,
  formatDateWithYear,
  getDeviceTimezone,
  localizeMomentDate,
  localizeMomentTime,
} from '@/utils/date';
import {
  AttachmentData,
  AttachmentFileExtension,
  AttachmentMimeType,
  AttachmentMimeTypeIcon,
  EmployeeContactInfo,
  InboundListItem,
  MatchedImageLocation,
  MimeTypeByExtension,
  OrderAgentWorkflowMode,
  OrderInboundAssignedEmployee,
  OrderInboundListFieldsFragment,
  OrderInboundOption,
  OrderInboundSection,
  OrderInboundTag,
  OrderInboundTranslations,
  OrderMetadata,
  OrderMetadataSegment,
  SearchInput,
  SearchInputFieldsFragmentWithTranslations,
  TextSegment,
  TranslationData,
  UnknownContactInfo,
  VoicemailWordTimestamp,
} from './types';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { FILTER_OPEN_STATUSES } from './order-inbounds-order-list/types';
import { formatPhoneNumber } from '@/utils/phone-number';
import {
  ImageMapArea,
  InboundAttachment,
} from '@/components/attachment-list/attachment-list-types';
import {
  OrderInboundListSearchInputFieldsFragment,
  OrderInbounds_ListQuery,
  SmartOrderFieldsFragment,
} from '@/types';
import {
  OrderInboundSource,
  SmartOrderProcessingStatus,
  SmartOrderSearchTermStatus,
  SmartOrderStatus,
} from '@/enums';

export const SAFE_AREA_PADDING = 60;
export const PAGE_CONTAINER_PADDING_HORIZONTAL = 16;

export const getMergeDisabledMessage = (restaurantName?: string | null) =>
  restaurantName
    ? i18n.t('order_inbounds.merge_disabled_no_other_inbounds', {
        restaurantName,
      })
    : i18n.t('order_inbounds.merge_disabled_message');

// Include ERROR for now so the UI can gracefully handle smart_order records
// that errored out when processing. We will eventually want to handle this as
// an error case, but for now group in with the loading cases
export const PROCESSING_STATUSES = new Set([
  SmartOrderProcessingStatus.REPROCESSING,
  SmartOrderProcessingStatus.PROCESSING,
  SmartOrderProcessingStatus.ERROR,
]);

export const WEB_MARGIN_TOP = 12;

const getEffectiveSearchInputs = (order: SmartOrderFieldsFragment) =>
  order.status === SmartOrderStatus.SUBMITTED ||
  order.status === SmartOrderStatus.REJECTED ||
  order.status === SmartOrderStatus.MEMORIZED
    ? []
    : order.search_inputs;

// Inputs that made it into the real order. The submit flow only sends list
// items with a matched variant pack and a non-zero quantity, and unmatched
// inputs are never pruned from the smart order after submission, so this is
// the closest client-side approximation of what was actually submitted.
const getSubmittedSearchInputs = (order: SmartOrderFieldsFragment) =>
  order.search_inputs.filter(
    (input) => !!input.search_term.variants_pack && input.quantity > 0,
  );

/**
 * Which of an order's search inputs to surface in metadata:
 * - `effective`: all inputs for open orders, none for closed orders (default)
 * - `submitted`: only inputs that were part of the submitted order
 * - `all`: every input regardless of order status
 */
export type SearchInputsMode = 'all' | 'effective' | 'submitted';

export interface OrderMetadataOptions {
  searchInputsMode?: SearchInputsMode;
}

const getSearchInputsForMode = (
  order: SmartOrderFieldsFragment,
  mode: SearchInputsMode = 'effective',
) => {
  switch (mode) {
    case 'all':
      return order.search_inputs;
    case 'submitted':
      return getSubmittedSearchInputs(order);
    default:
      return getEffectiveSearchInputs(order);
  }
};

export const ORIGINAL_INBOUND_SIDEBAR_MAX_WIDTH = 340;

export const PROCESSED_STATUSES = new Set([
  SmartOrderStatus.MEMORIZED,
  SmartOrderStatus.SUBMITTED,
  SmartOrderStatus.REJECTED,
]);

type OpenOrderCandidate = Pick<
  OrderMetadata,
  'createdAt' | 'smartOrderUUID' | 'status'
>;

export const getLatestOpenSmartOrderUUID = (
  orders: OpenOrderCandidate[],
): string | undefined => {
  let latest: OpenOrderCandidate | undefined;
  for (const order of orders) {
    if (PROCESSED_STATUSES.has(order.status)) {
      continue;
    }
    if (!latest || order.createdAt > latest.createdAt) {
      latest = order;
    }
  }
  return latest?.smartOrderUUID;
};

export const getWorkflowModeForStatuses = (
  statuses: SmartOrderStatus[],
): OrderAgentWorkflowMode =>
  statuses.some((status) => FILTER_OPEN_STATUSES.has(status))
    ? OrderAgentWorkflowMode.Submission
    : OrderAgentWorkflowMode.Correction;

export const AUDIO_MIME_TYPES = new Set([
  AttachmentMimeType.AAC,
  AttachmentMimeType.AMR,
  AttachmentMimeType.M4A,
  AttachmentMimeType.MP3,
  AttachmentMimeType.MP4_AUDIO,
  AttachmentMimeType.OGG,
  AttachmentMimeType.OPUS,
  AttachmentMimeType.THREE_GPP,
  AttachmentMimeType.WAV,
  AttachmentMimeType.WEBM_AUDIO,
  AttachmentMimeType.X_WAV,
]);

export function isAudioMimeType(mimeType: string | null | undefined): boolean {
  return mimeType
    ? AUDIO_MIME_TYPES.has(mimeType as AttachmentMimeType)
    : false;
}

export function getSegmentsForAttachmentIndex(
  segments: OrderMetadataSegment[],
  attachmentIndex: number,
): OrderMetadataSegment[] {
  return segments.filter(
    (segment) => segment.attachmentIndex === attachmentIndex,
  );
}

function safeDecodeURIComponent(
  value: string,
  onError: (error: unknown) => void,
): string {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    onError(e);
    return value;
  }
}

export function getFilenameFromUrl(url: string): string {
  const match = url.match(/([^/]+?)(\.[^.]+)?$/);
  if (!match) {
    return 'Audio';
  }
  const decodeInput = match[1] + (match[2] ?? '');
  return safeDecodeURIComponent(decodeInput, (e) =>
    captureException(e, { extra: { attachmentUrl: url, decodeInput } }),
  );
}

export function getAttachmentFileName(
  attachmentUrl: string,
  smartOrderUUID: string,
): string | null {
  const match = attachmentUrl.match(/([^/]+?)(-\d+)?(\.[^.]+)$/);
  if (!match) {
    return null;
  }
  const decodeInput = match[1] + match[3];
  return safeDecodeURIComponent(decodeInput, (e) =>
    captureException(e, {
      extra: {
        attachmentUrl,
        decodeInput,
        matchGroup1: match[1],
        matchGroup3: match[3],
        smartOrderUUID,
      },
    }),
  );
}

/**
 * Maps OrderMetadataSegments to TextSegments.
 * No fallback behavior - returns empty array if segments is empty.
 * Used by both voicemail and audio attachment processing.
 */
export function mapSegmentsToTextSegments(
  segments: OrderMetadataSegment[],
  translationLocale?: string,
): TextSegment[] {
  return segments.map<TextSegment>((segment) => {
    // Get display text with translation support
    const matchedText =
      translationLocale &&
      segment.searchInput?.result?.translations?.matched_text?.translation?.[
        translationLocale
      ]
        ? segment.searchInput.result.translations.matched_text.translation[
            translationLocale
          ]
        : translationLocale &&
            segment.translations?.matchedText?.translation?.[translationLocale]
          ? segment.translations.matchedText.translation[translationLocale]
          : (segment.matchedText ?? segment.searchInput?.matchedText ?? '');

    const timestamp =
      segment.startSeconds !== null
        ? {
            endSeconds: null,
            startSeconds: segment.startSeconds,
            word: matchedText,
          }
        : null;

    if (segment.searchInput && segment.searchInput.quantity > 0) {
      return {
        originalMatchedText:
          segment.matchedText ?? segment.searchInput.matchedText,
        searchInputUUID: segment.searchInput.uuid,
        text: matchedText,
        timestamp,
        type: 'match',
      };
    } else {
      return {
        originalMatchedText: null,
        text: matchedText,
        timestamp,
        type: 'normal',
      };
    }
  });
}

export const skeletonData: OrderInboundSection[] = [
  {
    data: [
      { title: '1', value: '1' },
      { title: '2', value: '2' },
      { title: '3', value: '3' },
    ],
    key: '1',
  },
  {
    data: [
      { title: '1', value: '1' },
      { title: '2', value: '2' },
      { title: '3', value: '3' },
    ],
    key: '2',
  },
];

interface OrderIdentifiers {
  id: string;
  key: string;
}
export const PROCESSING_SECTION_ID = '__PROCESSING__';
export const PROCESSING_SECTION_KEY = i18n.t('processing');

const inProcessingSection = (order: OrderInboundListFieldsFragment) => {
  return (
    order.processing_status === SmartOrderProcessingStatus.PROCESSING &&
    !order.restaurant
  );
};

function getOrderIdentifiers(
  order: OrderInboundListFieldsFragment,
): OrderIdentifiers {
  if (inProcessingSection(order)) {
    return {
      id: PROCESSING_SECTION_ID,
      key: PROCESSING_SECTION_KEY,
    };
  }
  if (order.restaurant) {
    return {
      id: order.restaurant.uuid,
      key: order.restaurant.name,
    };
  }

  const isProcessing = PROCESSING_STATUSES.has(
    order.processing_status as SmartOrderProcessingStatus,
  );

  const timestamp = getTime(order);

  // Contact info fields are only set on the smart_order if it's unknown contact.
  // If a contact is assigned, we can get the contact info from the employee.
  if (isProcessing) {
    const orderEmployee = getOrderEmployee(order);
    const email = order.email_address ?? orderEmployee?.email;
    const phone = order.phone_number ?? orderEmployee?.phone;
    const sourceValue =
      order.source === 'EMAIL'
        ? email
        : phone
          ? formatPhoneNumber(phone)
          : undefined;

    const processingKey = sourceValue ?? timestamp;

    return {
      id: processingKey,
      key: processingKey,
    };
  }

  const id = order.phone_number ?? order.email_address ?? order.created_at;
  const key = order.phone_number
    ? formatPhoneNumber(order.phone_number)
    : (order.email_address ?? timestamp);

  return {
    id,
    key,
  };
}

export type OrdersByCustomerMap = Record<string, OrderInboundSection>;

interface OrderInboundOptionsData {
  ordersByCustomer: OrdersByCustomerMap;
  sectionData: {
    data: OrderInboundOption[];
    key: null;
  }[];
}

export const getOrderInboundOptions = (
  data: OrderInbounds_ListQuery,
  threadCountByThreadId?: Record<string, number>,
  isEmailThreadsEnabled?: boolean,
): OrderInboundOptionsData => {
  const ordersByCustomer: OrdersByCustomerMap = {};
  const mappedMergedOrders: Record<string, boolean> = {};
  const mappedThreadOrders: Record<string, boolean> = {};

  // Map thread_id → merge_uuid for threads that are part of a merge group, so orphan replies
  // (same thread_id, no merge_uuid) can be absorbed into the right group regardless of array order.
  const threadIdToMergeUUID: Record<string, string> = {};
  if (isEmailThreadsEnabled) {
    data.smart_order.forEach((order) => {
      if (order.thread_id && order.merge_uuid) {
        threadIdToMergeUUID[order.thread_id] = order.merge_uuid;
      }
    });
  }

  data.smart_order.forEach((order) => {
    const { id, key } = getOrderIdentifiers(order);
    if (!ordersByCustomer[id]) {
      ordersByCustomer[id] = {
        data: [],
        key,
        metadata: {
          canMerge: false,
          restaurantUUID: order.restaurant ? id : null,
        },
      };
    }

    const mergeUUID = order.merge_uuid;
    const threadId = isEmailThreadsEnabled ? order.thread_id : null;
    if (mergeUUID) {
      // If this order hasn't already been merged and mapped, do so now
      if (!mappedMergedOrders[order.uuid]) {
        const { originalOrders } = mergeOrdersById(
          mergeUUID,
          data.smart_order,
          (orderId) => (mappedMergedOrders[orderId] = true),
        );
        ordersByCustomer[id].data.push(
          ...buildOptionsForOrderGroup(originalOrders, threadCountByThreadId),
        );
      }
    } else if (threadId) {
      const associatedMergeUUID = threadIdToMergeUUID[threadId];
      if (associatedMergeUUID) {
        // This order is an orphan reply for a thread that belongs to a merge group.
        // If the merge group hasn't been processed yet, process it now (which will absorb this order).
        // If it was already processed, mappedMergedOrders will have this order's uuid — skip it.
        if (!mappedMergedOrders[order.uuid]) {
          const { originalOrders } = mergeOrdersById(
            associatedMergeUUID,
            data.smart_order,
            (orderId) => (mappedMergedOrders[orderId] = true),
          );
          ordersByCustomer[id].data.push(
            ...buildOptionsForOrderGroup(originalOrders, threadCountByThreadId),
          );
        }
      } else if (!mappedThreadOrders[order.uuid]) {
        const { originalOrders } = mergeOrdersByThreadId(
          threadId,
          data.smart_order,
          (orderId) => (mappedThreadOrders[orderId] = true),
        );
        ordersByCustomer[id].data.push(
          ...buildOptionsForOrderGroup(originalOrders, threadCountByThreadId),
        );
      }
    } else {
      ordersByCustomer[id].data.push(
        getOptionFromOrderData(order, undefined, threadCountByThreadId),
      );
    }
  });

  // `data.smart_order` already arrives in the order the list should render --
  // the history query applies the sort the user picked in the filter panel,
  // and the legacy query orders created_at desc -- so ranking each option by
  // where its orders sit in that array is what carries the server's ordering
  // through. Bucketing into `ordersByCustomer` above and the merge passes both
  // reorder rows relative to the response, and one option can cover several
  // orders (a merge group, an email thread), so an option takes the position
  // of its earliest-appearing member.
  //
  // Sorting on created_at here instead would silently undo any non-default
  // sort: an OLDEST_FIRST page came back correct and was then flipped back to
  // newest-first, and CLOSEST_CUTOFF has no client-side equivalent at all.
  const rankByOrderUUID = new Map(
    data.smart_order.map((order, index) => [order.uuid, index] as const),
  );
  const rankOf = (option: OrderInboundOption) =>
    Math.min(
      ...(option.metadata?.orderUUIDs ?? []).map(
        (uuid) => rankByOrderUUID.get(uuid) ?? Infinity,
      ),
    );

  const items = Object.values(ordersByCustomer)
    .map((orders) => orders.data)
    .flat()
    .map((option) => ({ option, rank: rankOf(option) }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ option }) => option);

  return { ordersByCustomer, sectionData: [{ data: items, key: null }] };
};

// Rejecting a row rejects all of its open orders in one mutation with a single
// client-generated updated_at, so orders rejected together share the exact
// same timestamp.
export const getRejectedBatchKey = (updatedAt: string): string => {
  const parsed = new Date(updatedAt).getTime();
  return Number.isNaN(parsed) ? updatedAt : String(parsed);
};

// An email thread's replies can be closed independently: some submitted under
// different real orders (different order_uuid), others rejected in separate
// batches as they arrived. Split such a group into one option per closed
// outcome so the Closed/All list reads as an accurate history:
// - SUBMITTED orders with an order_uuid bucket by that order_uuid
// - REJECTED orders bucket by their reject batch (shared updated_at)
// Everything else (open/pending/unknown-contact, or a stray order_uuid on a
// non-submitted row) stays lumped into the shared '' bucket, preserving the
// single-item behavior for the open half of the group.
function buildOptionsForOrderGroup(
  originalOrders: OrderInboundListFieldsFragment[],
  threadCountByThreadId?: Record<string, number>,
): OrderInboundOption[] {
  const byOrderUUID = new Map<string, OrderInboundListFieldsFragment[]>();
  originalOrders.forEach((order) => {
    let key = '';
    if (order.status === SmartOrderStatus.SUBMITTED && order.order_uuid) {
      key = order.order_uuid;
    } else if (order.status === SmartOrderStatus.REJECTED) {
      key = `rejected:${getRejectedBatchKey(order.updated_at)}`;
    }
    const bucket = byOrderUUID.get(key) ?? [];
    bucket.push(order);
    byOrderUUID.set(key, bucket);
  });
  return Array.from(byOrderUUID.values()).map((bucketOrders) => {
    const mergedOrder = mergeOrders(bucketOrders);

    // If the display-base order (newest-first) has no merge_uuid -- e.g. it's
    // an orphan thread reply that arrived after the merge group -- restore it
    // from a sibling in this bucket so the detail view query can still find
    // the full group.
    if (!mergedOrder.merge_uuid) {
      const donor = bucketOrders.find((o) => o.merge_uuid);
      if (donor) {
        mergedOrder.merge_uuid = donor.merge_uuid;
      }
    }

    return getOptionFromOrderData(
      mergedOrder,
      bucketOrders,
      threadCountByThreadId,
    );
  });
}

function mergeOrdersById(
  mergeUUID: string,
  orders: OrderInboundListFieldsFragment[],
  trackOrder: (id: string) => void,
) {
  const mergedOrders: OrderInboundListFieldsFragment[] = [];
  const mergeGroupThreadIds = new Set<string>();

  orders.forEach((order) => {
    if (order.merge_uuid === mergeUUID) {
      trackOrder(order.uuid);
      mergedOrders.push(order);
      if (order.thread_id) {
        mergeGroupThreadIds.add(order.thread_id);
      }
    }
  });

  // Absorb orphan thread replies: new emails in the thread that arrived without the merge_uuid
  if (mergeGroupThreadIds.size > 0) {
    orders.forEach((order) => {
      if (
        !order.merge_uuid &&
        order.thread_id &&
        mergeGroupThreadIds.has(order.thread_id)
      ) {
        trackOrder(order.uuid);
        mergedOrders.push(order);
      }
    });
  }

  // Restore newest-first ordering: the two-pass approach appends orphan replies at the
  // end, but data.smart_order is already sorted created_at desc by the backend query.
  // Without this sort, firstOrder in mergeOrders would be an older merge-group order.
  if (mergeGroupThreadIds.size > 0) {
    mergedOrders.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  const originalOrders = [...mergedOrders];

  return {
    originalOrders,
  };
}

function mergeOrdersByThreadId(
  threadId: string,
  orders: OrderInboundListFieldsFragment[],
  trackOrder: (id: string) => void,
) {
  const threadOrders = orders.filter((order) => {
    if (order.thread_id === threadId) {
      trackOrder(order.uuid);
      return true;
    }
    return false;
  });
  const originalOrders = [...threadOrders];

  return {
    originalOrders,
  };
}

interface SmartOrderMergeFields {
  search_inputs: OrderInboundListSearchInputFieldsFragment[];
  search_text?: string | null;
}

export function mergeOrders<T extends SmartOrderMergeFields>(
  mergedOrders: ReadonlyArray<T>,
) {
  const [firstOrder, ...rest] = mergedOrders;
  const mergedOrder: T = {
    ...firstOrder!,
    search_inputs: [...firstOrder!.search_inputs],
  };

  const searchInput: Record<string, boolean> = {};

  rest.forEach((order) => {
    order.search_inputs.forEach((input) => {
      const key = `${input.search_term.term}-${input.pack_unit ?? 'unit'}`;
      const hasMoreRecentTerm = searchInput[key];

      if (!hasMoreRecentTerm) {
        searchInput[key] = true;
        mergedOrder.search_inputs.push(input);

        if (input.matched_text) {
          mergedOrder.search_text += `, ${input.matched_text}`;
        }
      }
    });
  });
  return mergedOrder;
}

export function getDeliveryText(orders: OrderInboundListFieldsFragment[]) {
  // Get the first order with a fulfillment date
  const firstOrder = orders.find((o) => o.fulfillment_at);
  if (!firstOrder) {
    return null;
  }
  const fulfillmentAt = firstOrder.fulfillment_at;
  let formattedFulfillmentDate: string | undefined = undefined;
  if (fulfillmentAt) {
    const resultMoment = adjustMomentForTimezone(
      fulfillmentAt,
      getDeviceTimezone(),
    );
    formattedFulfillmentDate = formatDateWithYear(resultMoment);
  }
  let deliveryText: string | undefined = undefined;

  if (formattedFulfillmentDate && firstOrder.fulfillment_type) {
    deliveryText = i18n.t(
      `fulfillment_header.type.${firstOrder.fulfillment_type.toLowerCase()}`,
      {
        date: formattedFulfillmentDate,
      },
    );
  }
  return deliveryText;
}

function getAttachmentTags(orders: OrderInboundListFieldsFragment[]) {
  const tags: OrderInboundTag[] = orders.flatMap(
    (order) =>
      order.attachments?.attachment_list.flatMap(
        (attachment: AttachmentData) => {
          // Exclude attachments not meant for viewing in the attachment list.
          // This includes attachments where is_for_viewing is false, as well as
          // EML files which are only used for formatting.
          if (
            !attachment.url ||
            attachment.is_for_viewing === false ||
            attachment.mime_type === AttachmentMimeType.EML
          ) {
            return [];
          }
          const urlParts = attachment.url.toLocaleLowerCase().split(/[\.\/]/g);
          const extension = (urlParts.pop() ??
            'DEFAULT') as AttachmentFileExtension; // Last segment
          const text = urlParts.pop() ?? ''; // Second last segment

          const extensionMimeType =
            MimeTypeByExtension[extension] ?? MimeTypeByExtension.DEFAULT;
          const mimeType = attachment.mime_type ?? extensionMimeType;
          const iconType =
            AttachmentMimeTypeIcon[mimeType] ??
            AttachmentMimeTypeIcon[AttachmentMimeType.UNKNOWN]; // Fallback icon type

          return {
            iconType,
            text,
          };
        },
      ) ?? [],
  );
  return tags;
}

function getContactNames(orders: OrderInboundListFieldsFragment[]) {
  const unknownContactInfo = getUnknownContactInfo(orders);
  if (unknownContactInfo) {
    return unknownContactInfo.emailAddress
      ? unknownContactInfo.emailAddress
      : unknownContactInfo.phoneNumber
        ? formatPhoneNumber(unknownContactInfo.phoneNumber)
        : null;
  }

  const uniqueEmployeeNames = getUniqueOrderEmployeeNames(orders);
  return uniqueEmployeeNames.length > 0 ? uniqueEmployeeNames.join(', ') : null;
}

export function orderHasUnknownContact(order: OrderInboundListFieldsFragment) {
  return order.status === SmartOrderStatus.UNKNOWN_CONTACT;
}

export function getOptionFromOrderData(
  order: OrderInboundListFieldsFragment,
  originalOrders: OrderInboundListFieldsFragment[] = [order],
  threadCountByThreadId?: Record<string, number>,
): OrderInboundOption {
  const time = getTime(order);
  const isProcessing = PROCESSING_STATUSES.has(
    order.processing_status as SmartOrderProcessingStatus,
  );

  let title: string;

  if (isProcessing) {
    title = `${i18n.t('processing')}...`;
  } else if (orderHasUnknownContact(order)) {
    const unknownContact = getContactNames(originalOrders);
    title = unknownContact ?? order.restaurant?.name ?? '';
  } else {
    title = order.restaurant?.name ?? '';
  }

  let subtitle = null;

  if (isProcessing) {
    subtitle = i18n.t('matching_in_progress');
  } else {
    subtitle = getOrderTitle(order);
  }

  const mergeUUID =
    originalOrders && originalOrders.length > 1 ? order.merge_uuid : null;
  const tags = getAttachmentTags(originalOrders);

  const deliveryText = getDeliveryText(originalOrders);
  if (deliveryText) {
    tags.push({
      text: deliveryText,
    });
  }

  let assignedEmployee:
    | NonNullable<OrderInboundOption['metadata']>['assignedEmployee']
    | undefined;

  if (order.assigned_employee) {
    assignedEmployee = {
      name:
        order.assigned_employee.supplier_employee_overrides?.[0]?.name ??
        order.assigned_employee.name,
      uuid: order.assigned_employee.uuid,
    };
  }

  const hasAttachments = originalOrders.some(
    (order) =>
      order.attachments &&
      order.attachments.attachment_list.filter(
        (attachment) =>
          attachment.is_for_viewing &&
          attachment.mime_type &&
          attachment.mime_type !== AttachmentMimeType.EML,
      ).length > 0,
  );

  return {
    ...(subtitle ? { subtitle } : {}),
    metadata: {
      assignedEmployee,
      contactName: getContactNames(originalOrders) ?? undefined,
      hasAttachments,
      hasMatches: hasMatches(order.search_inputs),
      hasUnknownContact: orderHasUnknownContact(order),
      isProcessing,
      mergeUUID: mergeUUID || null,
      numItems: originalOrders.reduce<number>(
        (acc, curr) =>
          acc + curr.search_inputs.filter((input) => input.quantity > 0).length,
        0,
      ),
      openOrderUUIDs: originalOrders
        .filter((o) => FILTER_OPEN_STATUSES.has(o.status as SmartOrderStatus))
        .map((o) => o.uuid),
      orderUUIDs: originalOrders.map((order) => order.uuid),
      rejectedUpdatedAt:
        order.status === SmartOrderStatus.REJECTED
          ? order.updated_at
          : undefined,
      restaurantName: order.restaurant ? order.restaurant.name : null,
      restaurantUUID: order.restaurant ? order.restaurant.uuid : null,
      source: order.source as OrderInboundSource,
      submittedOrderUUID:
        order.status === SmartOrderStatus.SUBMITTED
          ? (order.order_uuid ?? undefined)
          : undefined,
      supplierUUID: order.supplier_uuid,
      tags,
      threadCount: order.thread_id
        ? (threadCountByThreadId?.[order.thread_id] ?? 0)
        : undefined,
      threadIds: [
        ...new Set(
          originalOrders
            .map((o) => o.thread_id)
            .filter((id): id is string => Boolean(id)),
        ),
      ],
      time,
      timestamp: order.created_at,
      unknownContactInfo: getUnknownContactInfo(originalOrders),
      workflowMode: getWorkflowModeForStatuses(
        originalOrders.map((o) => o.status as SmartOrderStatus),
      ),
    },
    title,
    value: order.uuid,
  };
}

function getUnknownContactInfo(
  orders: OrderInboundListFieldsFragment[],
): UnknownContactInfo | undefined {
  const info = {} as UnknownContactInfo;
  let isUnknownContact = true;

  for (const order of orders) {
    // If at least one order has a restaurant, this is no longer an unknown contact
    if (isUnknownContact) {
      const isBackwardsCompatibleUnknownContact =
        !order.restaurant && order.status !== SmartOrderStatus.UNKNOWN_CONTACT;
      isUnknownContact =
        isBackwardsCompatibleUnknownContact || orderHasUnknownContact(order);
    }
    if (order.email_address && !info.emailAddress) {
      info.emailAddress = order.email_address;
    }
    if (order.phone_number && !info.phoneNumber) {
      info.phoneNumber = order.phone_number;
    }
    if (info.emailAddress && info.phoneNumber) {
      break;
    }
  }

  return isUnknownContact && (info.emailAddress || info.phoneNumber)
    ? info
    : undefined;
}

function getOrderEmployee(
  order: OrderInboundListFieldsFragment,
): EmployeeContactInfo | null {
  let email =
    order.created_by?.contact_info.email ??
    order.created_by?.contact_info.login_email ??
    order.email_address ??
    undefined;
  let phone =
    order.created_by?.contact_info.phone_number ??
    order.phone_number ??
    undefined;
  let name = order.created_by?.name ?? undefined;

  // Account for supplier employee overrides
  if (order.created_by?.supplier_employee_overrides) {
    // Get the first override with a matching supplier UUID
    const employeeOverride = order.created_by?.supplier_employee_overrides.find(
      (override) => override.supplier_uuid === order.supplier_uuid,
    );
    if (employeeOverride) {
      email = employeeOverride.contact_emails[0];
      phone = employeeOverride.contact_phone_numbers[0];
      name = employeeOverride.name ?? name;
    }
  }
  return email || phone || name
    ? {
        email,
        name,
        phone,
      }
    : null;
}

function getUniqueOrderEmployeeNames(
  orders: OrderInboundListFieldsFragment[],
): string[] {
  const uniqueNames = new Set<string>();
  for (const order of orders) {
    const name = getOrderEmployee(order)?.name;
    if (name) {
      uniqueNames.add(name);
    }
  }
  return Array.from(uniqueNames);
}

function getVoicemailUrl(order: SmartOrderFieldsFragment): string | undefined {
  if (order.source !== 'VOICEMAIL') {
    return undefined;
  }
  const attachments = order.attachments?.attachment_list;
  if (attachments) {
    for (const attachment of attachments) {
      if (
        isAudioMimeType(attachment.mime_type) ||
        (!attachment.mime_type && attachment.url?.endsWith('.mp3'))
      ) {
        return attachment.url;
      }
    }
  }
}

export function getOrderMetadataAttachments(
  order: SmartOrderFieldsFragment,
  options?: OrderMetadataOptions,
): InboundAttachment[] {
  const metadataAttachments: InboundAttachment[] = [];

  const attachments = order.attachments?.attachment_list;
  if (attachments) {
    const imageAreasMap: Record<string, ImageMapArea[]> = {};
    const searchInputsForAreas = getSearchInputsForMode(
      order,
      options?.searchInputsMode,
    );
    for (const input of searchInputsForAreas) {
      const matchedImageLocation: MatchedImageLocation =
        input.matched_image_location;
      if (matchedImageLocation) {
        const imageCoordinates = matchedImageLocation.coords;
        const imageURL = matchedImageLocation.url;
        if (!imageAreasMap[imageURL]) {
          imageAreasMap[imageURL] = [];
        }

        imageAreasMap[imageURL].push({
          coordinates: imageCoordinates,
          id: input.uuid,
        });
      }
    }

    for (let i = 0; i < attachments.length; i++) {
      const attachment = attachments[i];
      // we want to keep all of the images even if they aren't set to `is_for_viewing`
      if (
        [
          AttachmentMimeType.GIF,
          AttachmentMimeType.HEIC,
          AttachmentMimeType.JPG,
          AttachmentMimeType.PNG,
          AttachmentMimeType.WEBP,
        ].includes(attachment.mime_type) ||
        // Temporary fix for images that do not have a mime type
        attachment.url.endsWith('.jpg') ||
        attachment.url.endsWith('.jpeg')
      ) {
        if (
          Boolean(attachment.is_inline_attachment) &&
          Boolean(attachment.inbound_content_id)
        ) {
          metadataAttachments.push({
            inboundContentId: attachment.inbound_content_id,
            mimeType: attachment.mime_type,
            originalFilename: attachment.original_filename,
            type: 'INLINE_IMAGE',
            url: attachment.url,
          });
        }

        if (attachment.is_for_viewing) {
          metadataAttachments.push({
            areas: imageAreasMap[attachment.url] ?? [],
            mimeType: attachment.mime_type,
            originalFilename: attachment.original_filename,
            type: 'IMAGE_MAP',
            url: attachment.url,
          });
        }
      } else if (
        attachment.mime_type === AttachmentMimeType.HTML &&
        attachment.is_for_viewing
      ) {
        metadataAttachments.push({
          mimeType: AttachmentMimeType.HTML,
          originalFilename: attachment.original_filename,
          type: 'HTML',
          url: attachment.url,
        });
      } else if (
        attachment.mime_type === AttachmentMimeType.EML &&
        attachment.is_for_viewing
      ) {
        metadataAttachments.push({
          mimeType: AttachmentMimeType.EML,
          originalFilename: attachment.original_filename,
          type: 'EML',
          url: attachment.url,
        });
      } else if (AUDIO_MIME_TYPES.has(attachment.mime_type)) {
        metadataAttachments.push({
          attachmentIndex: i,
          mimeType: attachment.mime_type as AttachmentMimeType,
          originalFilename: attachment.original_filename,
          type: 'AUDIO',
          url: attachment.url,
        });
      }
    }
  }

  return metadataAttachments;
}

export function getOrderMetadataAssignedEmployee(
  order: SmartOrderFieldsFragment,
): OrderInboundAssignedEmployee | null {
  if (!order.assigned_employee) {
    return null;
  }

  return {
    name:
      order.assigned_employee.supplier_employee_overrides[0]?.name ??
      order.assigned_employee.name,
    uuid: order.assigned_employee.uuid,
  };
}

export function getOrderMetadataSegments(
  order: SmartOrderFieldsFragment,
  searchInputs: SearchInput[],
): OrderMetadataSegment[] {
  if (!order.smart_order_segments || order.smart_order_segments.length === 0) {
    return [];
  }

  const searchInputsByUUID = new Map<string, SearchInput>(
    searchInputs.map((input) => [input.uuid, input]),
  );

  return order.smart_order_segments.map<OrderMetadataSegment>((segment) => ({
    attachmentIndex: segment.attachment_index ?? null,
    matchedText: segment.matched_text ?? null,
    searchInput: segment.smart_order_search_input_uuid
      ? (searchInputsByUUID.get(segment.smart_order_search_input_uuid) ?? null)
      : null,
    smartOrderSearchInputUuid: segment.smart_order_search_input_uuid ?? null,
    sortOrder: segment.sort_order,
    startSeconds: segment.start_seconds ?? null,
    translations: segment.translations ?? null,
    uuid: segment.uuid,
  }));
}

export function getOrderMetadata(
  orders: SmartOrderFieldsFragment[],
  options?: OrderMetadataOptions,
): OrderMetadata[] {
  const uniqueEmployeeNames = getUniqueOrderEmployeeNames(orders);
  return orders.map<OrderMetadata>((order) => {
    const employee = getOrderEmployee(order);
    const metadataAttachments = getOrderMetadataAttachments(order, options);
    const voicemailRecordingUrl = getVoicemailUrl(order);
    const deliveryText = getDeliveryText([order]);
    const assignedEmployee = getOrderMetadataAssignedEmployee(order);

    const translations = order.translations
      ? Object.entries(order.translations).reduce<
          Record<string, TranslationData>
        >((acc, [key, property]) => {
          const translationData = property as TranslationData;
          acc[key] = translationData;
          return acc;
        }, {})
      : null;

    const rawSearchInputs = getSearchInputsForMode(
      order,
      options?.searchInputsMode,
    );
    const searchInputs = rawSearchInputs.map<SearchInput>((input) => ({
      itemNotes: input.item_notes ?? null,
      matchedImageLocation: input.matched_image_location ?? null,
      matchedText: input.matched_text ?? null,
      originalPackUnit: input.original_pack_unit,
      originalQuantity: input.original_quantity,
      packUnit: input.pack_unit ?? null,
      quantity: input.quantity,
      requestedItemCode: input.requested_item_code,
      requestedItemName: input.requested_item_name,
      result: input,
      searchTerm: input.search_term.term,
      status: input.search_term.status as SmartOrderSearchTermStatus,
      uuid: input.uuid,
    }));

    const segments = getOrderMetadataSegments(order, searchInputs);

    return {
      ...(metadataAttachments.length > 0 && {
        attachments: metadataAttachments,
      }),
      ...(deliveryText && { deliveryText }),
      ...(employee && { employee }),
      ...(order.fulfillment_at && { fulfillmentAt: order.fulfillment_at }),
      ...(order.fulfillment_type && {
        fulfillmentType: order.fulfillment_type,
      }),
      ...(order.merge_uuid && { mergeUUID: order.merge_uuid }),
      ...(order.search_text && { searchText: order.search_text }),
      ...(segments.length > 0 && { segments }),
      ...(order.translations && {
        translations,
      }),
      ...(voicemailRecordingUrl && {
        voicemailRecordingUrl,
      }),
      // This is a fallback for voicemail orders that were created before we added the smart order segments table.
      // We probably don't need this since all new orders coming in will associated segments, and all processed
      // orders won't show up in the i2 UI after they've been processed, but better to be safe than sorry.
      ...(order.voicemail_word_timestamps && {
        voicemailWordTimestamps: (
          order.voicemail_word_timestamps as {
            end_seconds: number;
            start_seconds: number;
            word: string;
          }[]
        ).map<VoicemailWordTimestamp>((curr) => ({
          endSeconds: curr.end_seconds,
          startSeconds: curr.start_seconds,
          word: curr.word,
        })),
      }),
      assignedEmployee,
      createdAt: order.created_at,
      fullDate: getFullDate(order),
      orderNotes: order.order_notes ?? undefined,
      originalFulfillmentAt: order.original_fulfillment_at,
      originalSentAt: order.original_sent_at,
      poNumber: order.po_number,
      processingStatus: order.processing_status as SmartOrderProcessingStatus,
      restaurantName: order.restaurant ? order.restaurant.name : null,
      restaurantUUID: order.restaurant ? order.restaurant.uuid : null,
      searchInputs,
      smartOrderUUID: order.uuid,
      source: order.source as OrderInboundSource,
      status: order.status as SmartOrderStatus,
      subject: order.subject,
      submittedOrderNote: order.order?.notes ?? undefined,
      submittedOrderUUID: order.order_uuid ?? undefined,
      supplierUUID: order.supplier_uuid,
      threadId: order.thread_id ?? undefined,
      time: getTime(order),
      uniqueEmployeeNames,
      workflowMode: getWorkflowModeForStatuses([
        order.status as SmartOrderStatus,
      ]),
    };
  });
}

// TRANSLATION UTILS //

function fuzzyMatchByLocale(
  translation: Record<string, string>,
  locale: string,
) {
  if (translation[locale]) {
    return translation[locale];
  }
  const localeLanguage = locale.replace('_', '-').split('-')[0];
  if (translation[localeLanguage]) {
    return translation[localeLanguage];
  }
  for (const language in translation) {
    if (language.startsWith(localeLanguage)) {
      return translation[language];
    }
  }
  return undefined;
}

export function getSearchTermTranslation(
  input: SearchInputFieldsFragmentWithTranslations | undefined | null,
  locale = i18n.locale,
) {
  if (!input) {
    return undefined;
  }
  const translation =
    input.search_term.translations?.original_search_term?.translation;
  if (!translation) {
    return undefined;
  }
  return fuzzyMatchByLocale(translation, locale);
}

export function getMatchedTextTranslation(
  input: SearchInputFieldsFragmentWithTranslations | undefined | null,
  locale = i18n.locale,
) {
  if (!input) {
    return undefined;
  }
  const translation = input.translations?.matched_text?.translation;
  if (!translation) {
    return undefined;
  }
  return fuzzyMatchByLocale(translation, locale);
}

export function getSearchTextTranslation(
  order: OrderInboundListFieldsFragment | OrderMetadata | undefined | null,
  locale = i18n.locale,
) {
  if (!order) {
    return undefined;
  }
  const translations: OrderInboundTranslations | undefined = order.translations;
  const translation = translations?.search_text?.translation;
  if (!translation) {
    return undefined;
  }
  return fuzzyMatchByLocale(translation, locale);
}

export function getSubjectTranslation(
  order: OrderMetadata | undefined | null,
  locale = i18n.locale,
) {
  if (!order) {
    return undefined;
  }
  const translations: OrderInboundTranslations | undefined = order.translations;
  const translation = translations?.subject?.translation;
  if (!translation) {
    return undefined;
  }
  return fuzzyMatchByLocale(translation, locale);
}

export function getOriginalPackUnitTranslation(
  input: SearchInputFieldsFragmentWithTranslations | undefined | null,
  locale = i18n.locale,
): string | undefined {
  if (!input) {
    return undefined;
  }

  const translation = input.translations?.original_pack_unit?.translation;
  if (!translation) {
    return undefined;
  }

  return fuzzyMatchByLocale(translation, locale);
}

export function getOriginalQuantityTranslation(
  input: SearchInputFieldsFragmentWithTranslations | undefined | null,
  locale = i18n.locale,
) {
  if (!input) {
    return undefined;
  }

  const translation = input.translations?.original_quantity?.translation;
  if (!translation) {
    return undefined;
  }

  return fuzzyMatchByLocale(translation, locale);
}

export function getRequestedItemCodeTranslation(
  input: SearchInputFieldsFragmentWithTranslations | undefined | null,
  locale = i18n.locale,
) {
  if (!input) {
    return undefined;
  }

  const translation = input.translations?.requested_item_code?.translation;
  if (!translation) {
    return undefined;
  }

  return fuzzyMatchByLocale(translation, locale);
}

export function hasSearchInputTranslations(
  input: SearchInputFieldsFragmentWithTranslations,
): boolean {
  if (!input.translations) {
    return false;
  }

  const translations = input.translations;
  const translationFields = [
    translations.matched_text,
    translations.original_pack_unit,
    translations.original_quantity,
    translations.requested_item_code,
  ];

  return translationFields.some((data) => {
    return data?.translation ? Object.keys(data.translation).length > 0 : false;
  });
}

export function hasTranslations(order: OrderMetadata): boolean {
  const translations: OrderInboundTranslations | undefined = order.translations;

  const hasOrderTranslations =
    translations &&
    Object.values(translations).some((data) => {
      const translationData = data as TranslationData;
      return translationData?.translation
        ? Object.keys(translationData?.translation).length > 0
        : false;
    });

  const hasInputTranslations = order.searchInputs.some((input) => {
    return input.result ? hasSearchInputTranslations(input.result) : false;
  });

  return hasOrderTranslations || hasInputTranslations;
}

// END OF TRANSLATION UTILS //

export function getTitleFromInput(
  input: SearchInput,
  showTranslation?: boolean,
) {
  const originalPackUnitTranslation = getOriginalPackUnitTranslation(
    input.result,
  );
  const originalQuantityTranslation = getOriginalQuantityTranslation(
    input.result,
  );
  const requestedItemCodeTranslation = getRequestedItemCodeTranslation(
    input.result,
  );
  const searchTermTranslation = getSearchTermTranslation(input.result);

  const originalPackUnit = input.originalPackUnit
    ? ` ${sanitizeInputText(input.originalPackUnit)}`
    : '';
  const originalQuantityAndPackUnit = input.originalQuantity
    ? `${sanitizeInputText(input.originalQuantity.toString())}${originalPackUnit}`
    : '';
  const originalRequestedItemName = input.requestedItemName
    ? `${sanitizeInputText(input.requestedItemName)}`
    : '';
  const originalRequestedItemCode = input.requestedItemCode
    ? `${sanitizeInputText(input.requestedItemCode)}`
    : '';

  const originalStructuredText = [
    originalQuantityAndPackUnit,
    originalRequestedItemName,
    originalRequestedItemCode,
  ]
    .filter(Boolean)
    .join(' • ');

  const translatedPackUnit = input.originalPackUnit
    ? ` ${sanitizeInputText(originalPackUnitTranslation ?? input.originalPackUnit)}`
    : '';
  const translatedQuantityAndPackUnit = input.originalQuantity
    ? `${sanitizeInputText(originalQuantityTranslation ?? input.originalQuantity.toString())}${translatedPackUnit}`
    : '';
  // Use search term translation for requested item name
  const translatedRequestedItemName = input.requestedItemName
    ? `${sanitizeInputText(searchTermTranslation ?? input.requestedItemName)}`
    : '';
  const translatedRequestedItemCode = input.requestedItemCode
    ? `${sanitizeInputText(requestedItemCodeTranslation ?? input.requestedItemCode)}`
    : '';

  // Build the translated structured text
  const translatedStructuredText = [
    translatedQuantityAndPackUnit,
    translatedRequestedItemName,
    translatedRequestedItemCode,
  ]
    .filter(Boolean)
    .join(' • ');

  const originalText =
    originalStructuredText || sanitizeInputText(input.searchTerm);

  // If we have translations that create a different structured text, show both
  if (
    translatedStructuredText &&
    translatedStructuredText !== originalStructuredText
  ) {
    if (showTranslation) {
      // When showing translations, put translated text first and original in parentheses
      return `${translatedStructuredText} (${originalText})`;
    } else {
      // When showing original, put original text first and translation in parentheses
      return `${originalText} (${translatedStructuredText})`;
    }
  }

  return originalText;
}

function hasMatches(inputs: OrderInboundListSearchInputFieldsFragment[]) {
  return inputs.length !== 0;
}

export function stripHtmlTags(text: string) {
  return text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
}

function sanitizeInputText(text?: string | null) {
  if (!text) {
    return '';
  }
  const textLowerCase = text.replace(/\s+/g, ' ').toLocaleLowerCase();
  return textLowerCase.charAt(0).toUpperCase() + textLowerCase.slice(1);
}

function getOrderTitle(order: OrderInboundListFieldsFragment) {
  if (inProcessingSection(order)) {
    return i18n.t('no_product_matches');
  }

  const translation = getSearchTextTranslation(order);
  const searchText = translation || order.search_text;
  if (searchText) {
    return sanitizeInputText(stripHtmlTags(searchText));
  }

  // Loop through the search inputs and get the (translated) matched text,
  // until we have two non-zero quantity inputs.
  let matchedText = '';
  let divider = '';
  let nonZeroQuantityCount = 0;
  for (const input of order.search_inputs) {
    if (input.quantity === 0) {
      continue;
    }
    nonZeroQuantityCount++;
    const translation = getMatchedTextTranslation(input);
    matchedText += `${divider}${translation ?? input.matched_text}`;
    divider = ', ';
    if (nonZeroQuantityCount === 2) {
      break;
    }
  }

  return matchedText
    ? sanitizeInputText(matchedText)
    : i18n.t('no_product_matches');
}

export function getTime(order: OrderInboundListFieldsFragment) {
  const orderCreatedAt = moment(order.created_at);
  const deviceTimezone = getDeviceTimezone();
  const localTime = orderCreatedAt.tz(deviceTimezone);
  const beginningOfDay = moment().tz(deviceTimezone).startOf('day');

  if (orderCreatedAt.isAfter(beginningOfDay)) {
    return localizeMomentTime(localTime);
  }
  const beginningOfYesterday = beginningOfDay.subtract(1, 'day');
  if (orderCreatedAt.isAfter(beginningOfYesterday)) {
    return i18n.t('yesterday');
  }
  return formatDate(localTime);
}

function getFullDate(order: SmartOrderFieldsFragment) {
  const dateStr = order.original_sent_at ?? order.created_at;

  // The ecomm app defaults to showing times in the restaurant's timezone
  // Within the inbounds stack, default to showing in device timezone
  const timeData = moment(dateStr).tz(getDeviceTimezone());
  return localizeMomentDate(timeData, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function getTermFromInput(
  input?: SearchInput,
  translationLocale?: string,
) {
  if (!input) {
    return '';
  }
  const { searchTerm, matchedText } = input;
  if (translationLocale) {
    return (
      getMatchedTextTranslation(input.result, translationLocale) ?? searchTerm
    );
  }
  return matchedText ?? searchTerm;
}

export function sanitizeOriginalContent(
  order: OrderMetadata,
  translationLocale?: string,
) {
  if (!order.searchText) {
    return '';
  }
  const translation = translationLocale
    ? getSearchTextTranslation(order, translationLocale)
    : null;
  let content = translation ?? order.searchText;
  // Normalize Unicode ellipsis globally so downstream matching treats '…' and '...' the same
  content = content.replace(/\u2026/g, '...');
  for (const input of order.searchInputs) {
    const matchedTextTranslation = translationLocale
      ? getMatchedTextTranslation(input.result, translationLocale)
      : null;
    const matchedText = matchedTextTranslation ?? input.matchedText;
    // For each matched text, strip any whitespace that is inside that text
    if (matchedText) {
      try {
        // Normalize ellipsis in the term so it matches content regardless of '…' vs '...'
        const normalizedMatchedText = matchedText.replace(/\u2026/g, '...');
        // When using the matched text to generate a regex, we need to first
        // escape any special characters that would make the regex invalid
        const escapedText = normalizedMatchedText.replace(
          /[-\/\\^$*+?.()|[\]{}]/g,
          '\\$&',
        );
        const broadMatchText = escapedText.replace(/\s+/g, '[\\s\\-–—_]*');
        const regex = new RegExp(broadMatchText, 'gi');
        content = content.replace(regex, normalizedMatchedText);
      } catch (e) {
        captureException(e, {
          extra: {
            smartOrderSearchInputUUID: input.result?.uuid,
            smartOrderUUID: order.smartOrderUUID,
          },
        });
      }
    }
  }
  return content;
}

const extractWordsOnly = (text: string) => {
  // Split on whitespace or hyphens to support VM timestamps of hyphenated words.
  // For example, "20-pound" instead of "20 pounds"
  return text.split(/[\s-]+/).reduce<string[]>((acc, word) => {
    const strippedWord = word.replace(/^\W+|\W+$/g, '');
    if (strippedWord) {
      acc.push(strippedWord);
    }
    return acc;
  }, []);
};

const trimNonWordCharacters = (text: string) => {
  return text.replace(/^[\W_]+|[\W_]+$/g, '');
};

export function getContentSegments(
  orderMetadata: OrderMetadata,
  translationLocale?: string,
): TextSegment[] {
  const segments: TextSegment[] = [];
  const originalSanitizedContent = sanitizeOriginalContent(
    orderMetadata,
    translationLocale,
  );
  let remainingText = originalSanitizedContent;

  if (!remainingText) {
    return segments;
  }

  // use segments if available
  if (orderMetadata.segments && orderMetadata.segments.length > 0) {
    for (const segment of orderMetadata.segments) {
      if (segment.searchInput && segment.searchInput.quantity > 0) {
        const matchedText =
          translationLocale &&
          translationLocale !== '' &&
          segment.searchInput.result?.translations?.matched_text?.translation?.[
            translationLocale
          ]
            ? segment.searchInput.result?.translations?.matched_text
                ?.translation?.[translationLocale]
            : (segment.matchedText ?? '');

        segments.push({
          originalMatchedText: segment.matchedText!,
          searchInputUUID: segment.searchInput.uuid,
          text: matchedText,
          timestamp: null,
          type: 'match',
        });
      } else {
        const matchedText =
          translationLocale &&
          translationLocale !== '' &&
          segment.translations?.matchedText?.translation?.[translationLocale]
            ? segment.translations?.matchedText?.translation[translationLocale]
            : (segment.matchedText ?? '');

        segments.push({
          originalMatchedText: null,
          text: matchedText,
          timestamp: null,
          type: 'normal',
        });
      }
    }

    return segments;
  }

  for (const input of orderMetadata.searchInputs) {
    const matchedText = getTermFromInput(input, translationLocale);
    const originalMatchedText = input.matchedText
      ? input.matchedText.replace(/\u2026/g, '...')
      : null;

    const matchTerm = input.quantity === 0 ? '' : matchedText;
    if (!matchTerm) {
      continue;
    }
    const normalizedMatchTerm = matchTerm.replace(/\u2026/g, '...');
    const loweredRemainingText = remainingText.toLowerCase();
    const loweredMatchTerm = normalizedMatchTerm.toLowerCase();
    const matchIndex = loweredRemainingText.indexOf(loweredMatchTerm);
    const matchEndIndex =
      matchIndex > -1 ? matchIndex + normalizedMatchTerm.length : -1;

    if (matchIndex > 0) {
      segments.push({
        originalMatchedText: null,
        text: remainingText.substring(0, matchIndex),
        timestamp: null,
        type: 'normal',
      });
    }

    if (matchIndex > -1) {
      segments.push({
        originalMatchedText,
        searchInputUUID: input.uuid,
        text: remainingText.substring(matchIndex, matchEndIndex),
        timestamp: null,
        type: 'match',
      });

      remainingText = remainingText.substring(matchEndIndex);
    }
  }

  if (remainingText) {
    segments.push({
      originalMatchedText: null,
      text: remainingText,
      timestamp: null,
      type: 'normal',
    });
  }

  return segments;
}

export const processVoicemailSegments = (
  orderMetadata: OrderMetadata,
  translationLocale?: string,
): TextSegment[] => {
  // If we have segments and a voicemail URL, use the shared mapping function
  if (
    orderMetadata.segments &&
    orderMetadata.segments.length > 0 &&
    orderMetadata.voicemailRecordingUrl
  ) {
    return mapSegmentsToTextSegments(orderMetadata.segments, translationLocale);
  }

  // if not, fall back to using the search_text
  const segments = getContentSegments(orderMetadata);
  const timestamps = orderMetadata.voicemailWordTimestamps;

  if (!timestamps || !orderMetadata.voicemailRecordingUrl) {
    return segments;
  }

  let wordsAddedSoFar = 0;

  if (translationLocale) {
    const unmatchedTimestamps: VoicemailWordTimestamp[] = [];

    const timestampsByOriginalMatchedText = segments.reduce<
      Record<string, VoicemailWordTimestamp>
    >((acc, segment) => {
      const segmentWords = extractWordsOnly(segment.text);
      if (segmentWords.length > 0) {
        if (segment.type === 'match') {
          acc[segment.text] = timestamps[wordsAddedSoFar];
        } else {
          unmatchedTimestamps.push(timestamps[wordsAddedSoFar]);
        }
      }
      wordsAddedSoFar += segmentWords.length;
      return acc;
    }, {});

    const translatedSegments = getContentSegments(
      orderMetadata,
      translationLocale,
    );
    return translatedSegments.reduce<TextSegment[]>((acc, segment) => {
      const segmentWords = extractWordsOnly(segment.text);
      if (segmentWords.length > 0) {
        segment.text = trimNonWordCharacters(segment.text);
        const timestamp: VoicemailWordTimestamp | undefined =
          timestampsByOriginalMatchedText[segment.originalMatchedText ?? ''];
        segment.timestamp = timestamp ?? unmatchedTimestamps.shift();
        acc.push(segment);
      }
      wordsAddedSoFar += segmentWords.length;
      return acc;
    }, []);
  }

  return segments.reduce<TextSegment[]>((acc, segment) => {
    const segmentWords = extractWordsOnly(segment.text);
    if (segmentWords.length > 0) {
      segment.text = trimNonWordCharacters(segment.text);
      segment.timestamp = timestamps[wordsAddedSoFar];
      acc.push(segment);
    }
    wordsAddedSoFar += segmentWords.length;
    return acc;
  }, []);
};

export function getOrderIndexInSectionData(
  sectionData: OrderInboundSection[],
  orderUUID: string,
  restaurantUUID: string,
): number {
  let index = -1;

  for (const section of sectionData) {
    // Add 1 for each section
    index += 1;

    // Check if this section contains the target order
    if (section.metadata?.restaurantUUID === restaurantUUID) {
      const orderIndex = section.data.findIndex(
        (order) => order.value === orderUUID,
      );

      if (orderIndex !== -1) {
        // Add 1 for each order up to and including the target order
        index += orderIndex + 1;
        return index;
      }
    }

    // Add 1 for each order in this section
    index += section.data.length;
  }

  // Order not found
  return -1;
}

export const useInboundMessageStyles = createStyles((theme) => ({
  text: {
    color: theme.colors.black,
  },
  textHighlighted: {
    backgroundColor: theme.sk.color.bg.highlight.secondary,
    borderRadius: 4,
    paddingHorizontal: 2,
    paddingVertical: 4,
  },
  textSelected: {
    backgroundColor: theme.sk.color.bg.highlight.primary,
  },
}));

export const getOrderSourceContact = (order: OrderMetadata) => {
  if (order.employee?.email && (order.subject || order.source === 'EMAIL')) {
    return order.employee?.email;
  }

  return order.employee?.phone
    ? formatPhoneNumber(order.employee?.phone)
    : null;
};

export const getRememberSelectionFromItem = (item?: InboundListItem | null) => {
  if (!item) {
    return true;
  }
  const status = item.metadata?.inputWithResult.status;
  return status !== SmartOrderSearchTermStatus.NO_REUSE;
};

export function isOptionDisabled(option: OrderInboundOption) {
  return Boolean(
    option.metadata?.hasUnknownContact || option.metadata?.isProcessing,
  );
}

export function getImageHighlightsForEML(
  order: OrderMetadata,
): Record<string, ImageMapArea[]> {
  const imageHighlights: Record<string, ImageMapArea[]> = {};

  if (!order.searchInputs || !order.attachments) {
    return imageHighlights;
  }

  // Create a set of inline image URLs for fast lookup
  const inlineImageUrls = new Set(
    order.attachments
      .filter((attachment) => attachment.type === 'INLINE_IMAGE')
      .map((attachment) => attachment.url),
  );

  // Only include highlights for images that are inline attachments
  for (const input of order.searchInputs) {
    const matchedImageLocation = input.matchedImageLocation;
    if (matchedImageLocation) {
      const { url, coords } = matchedImageLocation;

      // Only add highlight if this URL is an inline image
      if (inlineImageUrls.has(url)) {
        if (!imageHighlights[url]) {
          imageHighlights[url] = [];
        }

        imageHighlights[url].push({
          coordinates: coords,
          id: input.uuid,
        });
      }
    }
  }

  return imageHighlights;
}

/**
 * Gets the localized date string (according to the user's device timezone) for a given date.
 *
 * @param dateStr - The date string to convert
 * @returns The localized date string
 */
export function getLocalizedDateString(dateStr: string): string {
  const timeData = moment(dateStr).tz(getDeviceTimezone());

  return localizeMomentDate(timeData, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
